import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import apiClient from "../utils/apiClient";

const AVAILABLE_PERIOD_TYPES = [
  { key: "pre_germination", label: "Pre-germination" },
  { key: "direct_sowing", label: "Direct Sowing" },
  { key: "planting", label: "Planting" },
  { key: "setting", label: "Setting" },
  { key: "pruning", label: "Pruning" },
  { key: "harvest", label: "Harvest" },
  { key: "picking", label: "Picking" },
];

const CropTypeForm = ({ onSubmit, onCancel, onDelete, initialData = null }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    allowed_period_types: [],
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || "",
        description: initialData.description || "",
        allowed_period_types: initialData.allowed_period_types || [],
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePeriodTypeToggle = (periodKey) => {
    setFormData((prev) => {
      const periodTypes = prev.allowed_period_types.includes(periodKey)
        ? prev.allowed_period_types.filter((p) => p !== periodKey)
        : [...prev.allowed_period_types, periodKey];
      return { ...prev, allowed_period_types: periodTypes };
    });
  };

  const handleSubmit = async () => {
    const normalized = Object.fromEntries(
      Object.entries(formData).map(([k, v]) => [k, v === "" ? null : v])
    );
    onSubmit(normalized);
  };

  const handleDelete = async () => {
    if (onDelete) {
      onDelete(initialData.crop_type_uuid);
    }
    setDeleteDialogOpen(false);
  };

  return (
    <Box>
      <TextField 
        fullWidth 
        label="Name" 
        name="name" 
        value={formData.name} 
        onChange={handleChange} 
        margin="normal" 
        size="small" 
        required
      />
      <TextField 
        fullWidth 
        label="Description" 
        name="description" 
        value={formData.description} 
        onChange={handleChange} 
        margin="normal" 
        multiline 
        rows={2} 
        size="small" 
      />

      <Box sx={{ marginTop: 3 }}>
        <Typography variant="h6" gutterBottom>Allowed Period Types</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
          Select which period types crops of this type can have
        </Typography>
        <FormGroup>
          {AVAILABLE_PERIOD_TYPES.map((period) => (
            <FormControlLabel
              key={period.key}
              control={
                <Checkbox
                  checked={formData.allowed_period_types.includes(period.key)}
                  onChange={() => handlePeriodTypeToggle(period.key)}
                />
              }
              label={period.label}
            />
          ))}
        </FormGroup>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        <Box>
          {initialData && onDelete && (
            <Button 
              onClick={() => setDeleteDialogOpen(true)} 
              color="error" 
              className="vintage-button"
              sx={{ mr: 1 }}
            >
              Delete
            </Button>
          )}
          <Button onClick={onCancel} className="vintage-button">Cancel</Button>
        </Box>
        <Button variant="outlined" onClick={handleSubmit} className="vintage-button">
          {initialData ? "Update" : "Create"}
        </Button>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Crop Type</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the crop type "{initialData?.name}"? 
            This action cannot be undone and will also delete:
            <br/>• All crops of this type
            <br/>• All plot type associations with this crop type
            <br/><br/>
            <strong>Warning:</strong> This is a destructive action that cannot be reversed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} className="vintage-button">
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            className="vintage-button"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CropTypeForm;
